import { Button, Badge, cn } from '@design-system';
import { FlaskConical, Shield, Send, Heart, Loader2 } from 'lucide-react';
import { AdvisorWarningDialog } from '@/components/AdvisorWarningDialog';
import { IncompleteIntakeDialog } from '@/shared/components/incomplete-intake-dialog';
import { OnboardingOverview } from '@/pages/OnboardingOverview';
import { PageLink } from '@/components/PageLink';

import { useNavigateToPage } from '@client';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSendReminderStore } from '@/features/send-reminder';
import { EdjPilotRestricted } from '@/shared/components/edj-pilot-restricted';

import { findClientPartner, fullName as sharedFullName } from '@shared';
import { clientNeedsFullIntakeForm } from '@/shared/lib/utils';
import { useValidatedIntakeNavigation } from '@/shared/hooks/use-validated-intake-navigation';
import { INTAKE_FORM_INCOMPLETE, ERROR_OR_INCOMPLETE_STATUS_TAGS } from '@client';

function canViewResults(clientTags: string[]) {
  return !clientTags.some(tag => ERROR_OR_INCOMPLETE_STATUS_TAGS.includes(tag));
}

interface ClientNameCellProps {
  clients: Client[];
  client: Client;
  sessionAdvisor: Advisor;
}

export function ClientNameCell({ clients, client, sessionAdvisor }: ClientNameCellProps) {
  const navigateToPage = useNavigateToPage();
  const navigate = useNavigate();
  const [incompleteDialogOpen, setIncompleteDialogOpen] = useState(false);
  const { openWithClient } = useSendReminderStore();
  const hasResults = canViewResults(client.clientTags);
  const needsFullIntake = clientNeedsFullIntakeForm(client);
  const isDifferentAdvisor = sessionAdvisor.advisorId !== client.advisorId;
  const { validateAndDecide, isValidating } = useValidatedIntakeNavigation(client);

  const handleNavigateToClient = useCallback(async () => {
    const { needsFullIntake: freshNeedsFullIntake } = await validateAndDecide();
    if (freshNeedsFullIntake) {
      setIncompleteDialogOpen(true);
    } else {
      navigateToPage(OnboardingOverview, { clientId: client.clientId });
    }
  }, [navigateToPage, client.clientId, validateAndDecide]);

  const handleExploreQuotes = useCallback(() => {
    navigate(`/clients/${client.clientId}/onboarding/shop-insurance`);
  }, [navigate, client.clientId]);

  const handleSendReminder = useCallback(() => {
    openWithClient(client);
  }, [openWithClient, client]);

  const partner = findClientPartner(client, clients);

  const nameButton = (
    <Button
      size="sm"
      variant="ghost"
      disabled={!hasResults || isValidating}
      className={cn('text-primary-dark hover:bg-primary/5 h-8 px-2 font-medium', {
        'pr-1': client.isSampleClient,
      })}
    >
      {sharedFullName(client)}
      {isValidating && <Loader2 className="size-3 animate-spin" />}
      {client.isSampleClient && (
        <Badge variant="warning">
          <FlaskConical className="size-3" />
          Sample Client
        </Badge>
      )}
    </Button>
  );

  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex items-center gap-1">
        {!hasResults ? (
          nameButton
        ) : isDifferentAdvisor ? (
          <AdvisorWarningDialog
            title="Viewing another advisor's client"
            message="Any changes you make will affect the actual client record and be visible to both the client and their primary advisor."
            onContinue={handleNavigateToClient}
          >
            {nameButton}
          </AdvisorWarningDialog>
        ) : needsFullIntake ? (
          <span
            onClick={handleNavigateToClient}
            className={cn('cursor-pointer', isValidating && 'cursor-wait')}
          >
            {nameButton}
          </span>
        ) : (
          <PageLink
            to={OnboardingOverview}
            targetProps={{ clientId: client.clientId }}
          >
            {nameButton}
          </PageLink>
        )}
      </div>
      {partner && (
        <span className="text-muted-foreground flex items-center gap-1 pl-1 text-xs">
          <Heart className="size-3 fill-rose-400 text-rose-400" />
          Coupled with {sharedFullName(partner)}
        </span>
      )}
      {hasResults && client.isQuoteAvailable && (
        <EdjPilotRestricted>
          <Button
            size="sm"
            variant="outline"
            onClick={handleExploreQuotes}
            title={`Explore quotes for ${sharedFullName(client)}`}
            className="pl-1.5! h-7 w-fit self-start pr-2 text-xs md:opacity-0 md:group-hover:opacity-100"
          >
            <Shield className="size-3.5" />
            Explore Quotes
          </Button>
        </EdjPilotRestricted>
      )}
      {client.clientTags.includes(INTAKE_FORM_INCOMPLETE) && (
        <EdjPilotRestricted>
          <Button
          size="sm"
          variant="ghost"
          onClick={handleSendReminder}
          title={`Send reminder to ${sharedFullName(client)}`}
          className="pl-1.5! h-7 w-fit self-start pr-2 text-xs md:opacity-0 md:group-hover:opacity-100"
        >
          <Send className="size-3.5" />
          Send Reminder
          </Button>
        </EdjPilotRestricted>
      )}
      <IncompleteIntakeDialog
        client={client}
        open={incompleteDialogOpen}
        onOpenChange={setIncompleteDialogOpen}
      />
    </div>
  );
}
